import { reportWorkflow, newAttemptId } from "../observability/telemetry.js";
import { isExpectedValidationResponse } from "../observability/apiRequestContext.js";
import { Alert, Divider } from "@mui/material";
import HolosModelInputs from "../Components/HolosModelInputs";
import MainButton from "../Components/Buttons/MainButton";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Page from "../Components/Page";
import { useAuth } from "../Components/Auth/useAuth";
import LoadingButton from "../Components/Buttons/LoadingButton";
import {
  fetchCropYear,
  fetchFarm,
  fetchField,
  fetchHolosJob,
  buildLegacyHeaders,
  postHolosJob,
} from "../Components/axiosFetchers";
import { axiosInstance } from "../Components/axiosFetchers";
import CropYearStatusBadge from "../Components/CropYearStatusBadge";
import { getCompletionPercent } from "../Components/cropYearCompletion.js";
import { getCropYearStepPath } from "../utils/cropYearRoutes.js";
import {
  buildIssueSearch,
  getBlockingValidationIssues,
  getCropYearValidationIssues,
  getIssueRouteStep,
} from "../utils/cropYearValidation.js";
import {
  ResourceBreadcrumb,
  buildCropYearBreadcrumb,
  formatCropYearLabel,
} from "../Components/ResourceBreadcrumb.jsx";
import AggregatorSelectedGrowerPanel, {
  AggregatorScopeWarning,
  useSelectedAggregatorGrower,
} from "../Components/AggregatorSelectedGrowerPanel.jsx";
import {
  DATA_READ_SCOPE,
  DATA_WRITE_SCOPE,
  selectedGrowerHasScope,
} from "../portal/aggregatorGrowerContext.js";
import {
  HOLOS_UNSUPPORTED_CROP_MESSAGE,
  isHolosCropSupported,
} from "../constants/holos.js";
import { buildFieldContextQuickFacts } from "../utils/resourceQuickFacts.js";
import { formDraftKey } from "../utils/formDrafts.js";
import {
  holosJobFailureMessage,
  isCurrentCompletedHolosJob,
  waitForTerminalHolosJob,
} from "../utils/holosAnalysisGeneration.js";

export default function ReviewForm({ requireSelectedGrower = false }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [pageError, setPageError] = useState("");
  const [missingFields, setMissingFields] = useState([]);
  const { farmId, fieldId, cropyearId } = useParams();
  const { user, impersonatedEmail } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const selectedAggregatorGrower = useSelectedAggregatorGrower();
  const selectedAggregatorGrowerPublicId =
    selectedAggregatorGrower?.grower_profile_public_id || "";
  const canReadSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_READ_SCOPE);
  const canWriteSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_WRITE_SCOPE);
  const navigate = useNavigate();

  const [startupComplete, setStartupComplete] = useState(false);
  const [loadedReviewKey, setLoadedReviewKey] = useState("");
  const [startupError, setStartupError] = useState(null);
  const [startupAttempt, setStartupAttempt] = useState(0);
  const [farm, setFarm] = useState(null);
  const [field, setField] = useState(null);
  const [cropYear, setCropYear] = useState(null);
  const reviewKey = formDraftKey(
    { user, impersonatedEmail, growerPublicId: selectedAggregatorGrowerPublicId },
    `review:${farmId}:${fieldId}:${cropyearId}`
  );
  const reviewLoaded = startupComplete && loadedReviewKey === reviewKey && canReadSelectedGrower;
  const currentStartupError = startupError?.key === reviewKey ? startupError.message : "";
  const reviewIdentityKey = JSON.stringify([reviewKey, canReadSelectedGrower, canWriteSelectedGrower]);
  const statusRequestRef = useRef(null);
  // Calculations use the captured input version; record replies are ordered separately below.
  const reviewScopeKey = JSON.stringify([reviewIdentityKey, cropYear?.current_version_id ?? null]);
  const activeReviewContextRef = useRef({ key: reviewScopeKey, identity: { key: reviewIdentityKey } });
  if (activeReviewContextRef.current.key !== reviewScopeKey) {
    const identity = activeReviewContextRef.current.identity.key === reviewIdentityKey
      ? activeReviewContextRef.current.identity : { key: reviewIdentityKey };
    if (identity !== activeReviewContextRef.current.identity) statusRequestRef.current = null;
    activeReviewContextRef.current = { key: reviewScopeKey, identity };
  }
  const reviewContext = activeReviewContextRef.current;
  const reviewIdentity = reviewContext.identity;

  useEffect(() => {
    setSuccess(false);
    setLoading(false);
    setPageError("");
  }, [reviewContext]);

  useEffect(() => { setSavingStatus(false); }, [reviewIdentity]);

  document.title = "Review Page - Field To Market Canada";

  const validationIssues = getCropYearValidationIssues(cropYear || {});
  const finalizedIssues = getBlockingValidationIssues(
    cropYear || {},
    "finalized"
  );
  const hasExistingAnalysis = Boolean(cropYear?.analysis_id);
  const analysisIsCurrent = Boolean(cropYear?.analysis_is_current);
  const admission = cropYear?.analysis_readiness;
  const canGenerate = Boolean(cropYear?.can_run_analysis) && admission?.eligible !== false;
  const isSupportedCrop = isHolosCropSupported(
    cropYear?.crop_this_year, cropYear?.holos_supported_crops
  );

  function viewExistingAnalysis() {
    navigate(
      `/farm/${farmId}/field/${fieldId}/cropyear/${cropyearId}/analysis`
    );
  }

  function applySavedReviewRecord(saved, requestIdentity = reviewIdentity) {
    if (activeReviewContextRef.current.identity !== requestIdentity || String(saved?.id) !== String(cropyearId)) return;
    setCropYear(current => {
      if (activeReviewContextRef.current.identity !== requestIdentity) return current;
      const currentVersion = current?.current_version_id;
      const savedVersion = saved?.current_version_id;
      if (currentVersion != null && (!Number.isInteger(savedVersion) || savedVersion < currentVersion)) return current;
      return saved;
    });
  }

  function handleJumpToIssue(issue) {
    const step = getIssueRouteStep(issue);
    if (step === "farm" || step === "field") {
      navigate(step === "farm" ? `/farm/${farmId}/form` : `/farm/${farmId}/field/${fieldId}/form`);
      return;
    }
    const path = getCropYearStepPath(
      {
        farmId,
        fieldId,
        cropyearId,
      },
      getIssueRouteStep(issue)
    );

    navigate(`${path}${buildIssueSearch(issue)}`);
  }

  async function handleGenerateAnalysis() {
    if (!reviewLoaded) return;
    const requestReviewContext = reviewContext;
    const isCurrentReview = () => activeReviewContextRef.current === requestReviewContext;
    if (!canGenerate) {
      setPageError("Review the analysis requirements below before generating an analysis.");
      return;
    }
    if (!isSupportedCrop) {
      setPageError(HOLOS_UNSUPPORTED_CROP_MESSAGE);
      return;
    }
    if (loading) return;
    if (!canWriteSelectedGrower) {
      setPageError("This grower link cannot generate analysis.");
      return;
    }
    if (success) {
      navigate(
        `/farm/${farmId}/field/${fieldId}/cropyear/${cropyearId}/analysis`
      );
      setLoading(false);
      setSuccess(false);
      return;
    }

    if (!loading) {
      setSuccess(false);
      setLoading(true);

      const attemptId = newAttemptId();
      try {
        setPageError("");
        setMissingFields(cropYear?.missing_analysis_fields || []);

        let holosJob = await fetchHolosJob(cropyearId, userRef.current);
        if (!isCurrentReview()) return;
        if (
          !isCurrentCompletedHolosJob(
            holosJob,
            cropYear?.current_version_id
          )
        ) {
          holosJob = await postHolosJob(cropyearId, userRef.current);
          if (!isCurrentReview()) return;
          holosJob = await waitForTerminalHolosJob({
            initialJob: holosJob,
            fetchJob: () => {
              if (!isCurrentReview()) return Promise.reject(new Error("Review context changed."));
              return fetchHolosJob(cropyearId, userRef.current);
            },
          });
          if (!isCurrentReview()) return;
        }

        if (
          !isCurrentCompletedHolosJob(
            holosJob,
            cropYear?.current_version_id
          )
        ) {
          throw new Error(holosJobFailureMessage(holosJob));
        }

        await axiosInstance.post(
          `${process.env.REACT_APP_API_URL}/cropyears/${cropyearId}/analysis`,
          {},
          {
            headers: buildLegacyHeaders(userRef.current),
          }
        );
        if (!isCurrentReview()) return;
        reportWorkflow({workflow:"analysis",outcome:"succeeded",reason:"completed",attemptId});
        setSuccess(true);
      } catch (error) {
        if (!isCurrentReview()) return;
        reportWorkflow({workflow:"analysis",outcome:isExpectedValidationResponse(error.response) ? "blocked" : "failed",reason:isExpectedValidationResponse(error.response) ? "missing_inputs" : "model_terminal_failure",attemptId,requestId:error.response?.headers?.["x-request-id"]});
        console.error(error);
        const responseDetail = error?.response?.data?.detail;
        if (
          responseDetail &&
          typeof responseDetail === "object" &&
          Array.isArray(responseDetail.missing_analysis_fields)
        ) {
          setMissingFields(responseDetail.missing_analysis_fields);
        }
        const detailMessage =
          typeof responseDetail === "string"
            ? responseDetail
            : responseDetail?.message;
        setPageError(
          detailMessage ||
            error?.message ||
            "The Holos calculation could not be completed. Please try again."
        );
      } finally {
        if (isCurrentReview()) setLoading(false);
      }
    }
  }

  async function updateDataStatus(nextStatus) {
    if (!reviewLoaded) return;
    const requestIdentity = reviewIdentity;
    if (!canWriteSelectedGrower) {
      setPageError("This grower link cannot update crop-year status.");
      return;
    }

    const request = {};
    statusRequestRef.current = request;
    setSavingStatus(true);
    setPageError("");

    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/farms/${farmId}/fields/${fieldId}/cropyears/${cropyearId}`,
        {
          data_status: nextStatus,
        },
        {
            headers: buildLegacyHeaders(userRef.current),
        }
      );

      applySavedReviewRecord(response.data, requestIdentity);
    } catch (error) {
      if (activeReviewContextRef.current.identity !== requestIdentity || statusRequestRef.current !== request) return;
      console.error(error);
      setPageError("We couldn't update the crop year status.");
    } finally {
      if (activeReviewContextRef.current.identity === requestIdentity && statusRequestRef.current === request) {
        statusRequestRef.current = null;
        setSavingStatus(false);
      }
    }
  }

  useEffect(() => {
    let active = true;
    setStartupComplete(false);
    setStartupError(null);
    setFarm(null);
    setField(null);
    setCropYear(null);
    setSuccess(false);
    setLoading(false);
    setSavingStatus(false);
    setPageError("");
    setMissingFields([]);
    if (
      requireSelectedGrower &&
      (!selectedAggregatorGrowerPublicId || !canReadSelectedGrower)
    ) {
      return () => { active = false; };
    }

    async function startupReviewPage() {
      const attemptId = newAttemptId();
      try {
        const [nextFarm, nextField, nextCropYear] = await Promise.all([
          fetchFarm(farmId, userRef.current),
          fetchField(farmId, fieldId, userRef.current),
          fetchCropYear(farmId, fieldId, cropyearId, userRef.current),
        ]);
        if (!active) return;
        setFarm(nextFarm);
        setField(nextField);
        setCropYear(nextCropYear);
        setLoadedReviewKey(reviewKey);
        setStartupComplete(true);
      } catch (error) {
        if (!active) return;
        const status = error?.response?.status;
        const message = status === 404
          ? "This crop year is unavailable. It may have been removed or the link may be out of date."
          : status === 403
          ? "You don't have access to this crop year in the current workspace."
          : status === 401
          ? "Your session needs to reconnect before this crop year can load. Your saved data is unchanged."
          : "We couldn't load this crop year. Your saved data is unchanged. Check your connection and retry.";
        setStartupError({ key: reviewKey, message });
        reportWorkflow({ workflow: "analysis", outcome: "failed", reason: "request_failed", attemptId, requestId: error?.response?.headers?.["x-request-id"] || error?.config?._ftmcTelemetry?.requestId });
      }
    }

    startupReviewPage();
    return () => { active = false; };
  }, [
    canReadSelectedGrower,
    cropyearId,
    farmId,
    fieldId,
    requireSelectedGrower,
    selectedAggregatorGrowerPublicId,
    reviewKey,
    startupAttempt,
  ]);

  useEffect(() => {
    if (startupComplete) {
      setSuccess(false);
      setLoading(false);
      setMissingFields(cropYear?.missing_analysis_fields || []);
    }
  }, [cropYear, startupComplete]);

  return (
    <Page
      title={reviewLoaded ? formatCropYearLabel(cropYear) : "Review and Calculate"}
      headerBorderColor="border-[#34a853]"
      headerVariant="hybrid"
      contentWidth="wide"
      showQuickFacts={reviewLoaded}
      quickFactsVariant="inline-card"
      introContent={
        reviewLoaded && (farm?.name || field?.name) ? (
          <p>{[farm?.name, field?.name].filter(Boolean).join(" / ")}</p>
        ) : null
      }
      quickFactsData={[
        ...buildFieldContextQuickFacts({ farm, field, auditRecord: cropYear }),
        { label: "Crop", value: cropYear?.crop_this_year || null },
        { label: "Crop Year", value: cropYear?.crop_year || null },
      ]}
    >
      <div id="reviewFormId" className="w-full text-[rgb(102,102,102)]">
        {requireSelectedGrower ? (
          <div className="mb-5">
            <AggregatorSelectedGrowerPanel
              selectedGrower={selectedAggregatorGrower}
            />
          </div>
        ) : null}
        {requireSelectedGrower &&
        selectedAggregatorGrowerPublicId &&
        !canReadSelectedGrower ? (
          <AggregatorScopeWarning
            message="This selected grower link cannot view review or analysis records."
            requiredScope={DATA_READ_SCOPE}
          />
        ) : null}
        {requireSelectedGrower &&
        (!selectedAggregatorGrowerPublicId || !canReadSelectedGrower) ? null : !reviewLoaded ? (
          currentStartupError ? (
            <Alert severity="error" action={<MainButton text="Retry loading review" onClick={() => setStartupAttempt(value => value + 1)} />}>
              {currentStartupError}
            </Alert>
          ) : <p role="status">Loading crop year…</p>
        ) : (
          <>
        <ResourceBreadcrumb
          ariaLabel="Crop year location"
          items={buildCropYearBreadcrumb({
            cropYear,
            cropyearId,
            farm,
            farmId,
            field,
            fieldId,
          })}
        />
        <div className="flex w-full flex-col lg:flex-row">
          <div>
          <h3 className="text-[24px] font-semibold text-slate-800">
            Review and Calculate
          </h3>
          <Divider sx={{ marginBottom: 2, marginTop: 1 }} />
          <Alert severity="info" className="mb-5 max-w-[820px]">
            Holos needs the crop year, crop, yield and yield unit, plus the farm
            province and field area and location. Enter fertilizer, tillage and
            irrigation water when applicable; leaving fertilizer blank uses zero
            added nutrients. Optional soil, climate, harvest and energy inputs can change
            the result. Crop history, equipment details, measured fuel and 4R
            practice records are retained for future use and are not required
            for the current Holos output. Recorded litres and hours are not converted
            to Holos energy. You can set diesel energy (GJ/ha) in Advanced crop model
            inputs below; without an override, this input is zero.
          </Alert>

          <div>
            <p className="font-bold">1. Confirm Data Status</p>
            <p className="max-w-[700px]">
              Draft crop years can be saved with partial information and
              finished later. Once the required analysis fields are present, the
              crop year becomes ready. Finalized is a separate reporting choice
              you can make when you consider the record complete.
            </p>
            <div className="pl-[40px] pb-[24px] mt-4">
              <div className="mb-3 flex items-center gap-3">
                <p className="text-[18px]">
                  Current Status: {cropYear?.data_status || "PROVISIONAL"}
                </p>
                <CropYearStatusBadge cropYear={cropYear || {}} />
              </div>
              <p className="mb-4 max-w-[650px] text-sm">
                Required crop-year inputs: {getCompletionPercent(cropYear || {})}%. Workflow
                state: {cropYear?.workflow_state || "DRAFT"}.{" "}
                {cropYear?.can_run_analysis
                  ? "The required crop-year inputs are present. Analysis eligibility is checked separately below."
                  : "This crop year is still a draft and needs a few more fields before analysis can run."}
              </p>
              {finalizedIssues.length > 0 && (
                <p className="mb-4 max-w-[650px] text-sm text-amber-700">
                  Finalization is blocked until {finalizedIssues.length} issue
                  {finalizedIssues.length === 1 ? "" : "s"} are addressed or
                  removed.
                </p>
              )}
              <MainButton
                disabled={
                  savingStatus ||
                  !canWriteSelectedGrower ||
                  (cropYear?.data_status !== "FINALIZED" &&
                    finalizedIssues.length > 0)
                }
                text={
                  cropYear?.data_status === "FINALIZED"
                    ? "Mark as Provisional"
                    : "Mark as Finalized"
                }
                onClick={() =>
                  updateDataStatus(
                    cropYear?.data_status === "FINALIZED"
                      ? "PROVISIONAL"
                      : "FINALIZED"
                  )
                }
              />
            </div>
          </div>
          {cropYear && <HolosModelInputs cropYear={cropYear} farmId={farmId} fieldId={fieldId} user={user} canWrite={canWriteSelectedGrower} onSaved={saved => applySavedReviewRecord(saved)} />}
          <div>
            <p className="font-bold">2. Calculate Fieldprint Analysis</p>
            <p className="max-w-[700px]">
              Once the required data is present, the portal runs Holos and
              saves the Fieldprint Analysis only after the modeled result is
              complete. No estimated or placeholder result is generated.
            </p>
            <div className="pl-[40px] pb-[24px] mt-4">
              {hasExistingAnalysis && !analysisIsCurrent ? (
                <Alert severity="warning" className="mb-4 max-w-[700px]">
                  The saved analysis belongs to an earlier crop-year version.
                  Regenerate it to use the inputs currently shown in this crop
                  year.
                </Alert>
              ) : null}
              {admission?.eligible === false && (
                <Alert severity="warning" className="mb-4 max-w-[700px]">
                  <strong>Attention required before analysis</strong>
                  <ul className="list-disc pl-5">
                    {admission.reasons.map((reason, index) => <li key={`${reason.code}-${index}`}>{reason.message}</li>)}
                  </ul>
                  <p>Your saved inputs are retained. Correct the indicated inputs or review the model settings above.</p>
                </Alert>
              )}
              {canGenerate ? (
                <div className="flex flex-wrap items-center gap-3">
                  <LoadingButton
                    text={
                      hasExistingAnalysis
                        ? "Regenerate Fieldprint Analysis"
                        : "Generate Your Fieldprint Analysis"
                    }
                    successText={"Go to Analysis Page"}
                    handleButtonClick={handleGenerateAnalysis}
                    loading={loading}
                    disabled={
                      !canWriteSelectedGrower ||
                      !isSupportedCrop
                    }
                    success={success}
                  />
                  {loading ? (
                    <p className="max-w-[420px] text-sm text-slate-600">
                      Running the Holos model. This can take a few minutes; keep
                      this page open until the analysis is ready.
                    </p>
                  ) : null}
                  {hasExistingAnalysis && !success ? (
                    <MainButton
                      text={
                        analysisIsCurrent
                          ? "View Current Analysis"
                          : "View Prior Analysis"
                      }
                      onClick={viewExistingAnalysis}
                    />
                  ) : null}
                </div>
              ) : (
                <MainButton
                  disabled={true}
                  text={admission?.eligible === false ? "Review Requirements Before Analysis" : "Complete Draft Before Analysis"}
                />
              )}
              {cropYear?.crop_this_year &&
              !isSupportedCrop ? (
                <Alert severity="warning" className="mt-4 max-w-[700px]">
                  {HOLOS_UNSUPPORTED_CROP_MESSAGE}
                </Alert>
              ) : null}
              {!canWriteSelectedGrower ? (
                <div className="mt-4 max-w-[550px]">
                  <AggregatorScopeWarning
                    message="This selected grower link can view the review page but cannot finalize status, generate analysis, or start Holos jobs."
                    requiredScope={DATA_WRITE_SCOPE}
                  />
                </div>
              ) : null}
              {missingFields.length > 0 && (
                <div className="mt-4 max-w-[550px] rounded border border-amber-200 bg-amber-50 p-4 text-sm">
                  <p className="mb-2 font-bold">Still needed before analysis</p>
                  <ul className="list-disc pl-5">
                    {missingFields.map((fieldName) => (
                      <li key={fieldName}>{fieldName.replaceAll("_", " ")}</li>
                    ))}
                  </ul>
                </div>
              )}
              {validationIssues.length > 0 && (
                <div className="mt-4 max-w-[760px] rounded border border-slate-200 bg-slate-50 p-4 text-sm">
                  <p className="mb-2 font-bold text-slate-800">
                    Complete before analysis
                  </p>
                  <p className="mb-3 text-slate-600">
                    You can save a draft now. Complete these items before running
                    analysis.
                  </p>
                  <div className="flex flex-col gap-3">
                    {validationIssues.map((issue, index) => (
                      <div
                        key={`${issue.code}-${issue.field_path || index}`}
                        className="rounded border border-slate-200 bg-white p-3"
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <p className="font-semibold text-slate-800">
                              {issue.message}
                            </p>
                            {issue.detail && (
                              <p className="mt-1 text-slate-600">
                                {issue.detail}
                              </p>
                            )}
                            <p className="mt-2 text-xs uppercase tracking-[0.08em] text-slate-500">
                              {issue.code}
                              {issue.resolution?.can_remove_row
                                ? " • You can complete this row or remove it."
                                : ""}
                            </p>
                          </div>
                          {issue?.ui_target?.step && (
                            <MainButton
                              text={issue.ui_target.step === "farm" ? "Edit Farm" : issue.ui_target.step === "field" ? "Edit Field" : "Go to Input"}
                              onClick={() => handleJumpToIssue(issue)}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {pageError && (
                <p className="mt-3 max-w-[550px] text-sm text-[#c62828]">
                  {pageError}
                </p>
              )}
            </div>
          </div>
        </div>

          <div className="pb-16 lg:ml-10">
          <h4 className="text-[24px] font-semibold text-slate-800">Help</h4>
          <Divider sx={{ marginBottom: 2, marginTop: 1 }} />
          <div className="mb-6">
            <p className="font-bold">When should I finalize my data?</p>
            <p className="max-w-[500px]">
              Finalize a crop year once you consider the record complete and
              reviewed. Draft and provisional records can still be edited at any
              time.
            </p>
          </div>
          <div className="mb-6">
            <p className="font-bold">What happens when I finalize my data?</p>
            <p className="max-w-[500px]">
              Finalized is the clearest signal that the crop year is ready to be
              relied on for reporting. It does not stop you from switching back
              to provisional if more edits are needed.
            </p>
          </div>
          <div>
            <p className="font-bold">
              Can I revert my data back to provisional status?
            </p>
            <p className="max-w-[500px]">
              Yes. If more information comes in or something needs to be
              corrected, set the crop year back to provisional, make the edits,
              and finalize it again when you are satisfied.
            </p>
          </div>
          </div>
        </div>
          </>
        )}
      </div>
    </Page>
  );
}
